import { Accordion, AccordionDetails, AccordionSummary, accordionSummaryClasses, Box, Button, Checkbox, Divider, FormControlLabel, FormHelperText, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, useWatch } from 'react-hook-form';
import { Duration_Type_Enum, Preservation_Method_Enum } from '@kheops/graphql';
import NutritionFactsForm from '../info/NutritionFactsForm';
import { DraftProduct } from '../state';
import { IntercomArticlesIds } from '../../../common/utils/intercom.articles.enum';
import { isNonFoodProduct } from '../../common/products.utils';

interface ProductFormSpecificationsFieldsParam {
  control: Control<DraftProduct, unknown, DraftProduct>;
}

export default function ProductFormSpecificationsFields({ control }: ProductFormSpecificationsFieldsParam): React.JSX.Element {
  const { t } = useTranslation(['common', 'products', 'validation']);
  const [conservationExpanded, setConservationExpanded] = useState(false);
  const [ingredientsAllergensExpanded, setIngredientsAllergensExpanded] = useState(false);
  const [nutritionalInfoExpanded, setNutritionalInfoExpanded] = useState(false);

  const isExemptFromIngredientList = useWatch({
    control,
    name: 'is_exempt_from_ingredient_list',
  });

  const hasNoAllergenDeclaration = useWatch({
    control,
    name: 'has_no_allergen_declaration',
  });

  const subFamily = useWatch({ control, name: 'sub_family' });

  const isFoodProduct = useMemo((): boolean => {
    return !isNonFoodProduct(subFamily);
  }, [subFamily]);

  const vatRateValues = useMemo(
    () =>
      [2.1, 5.5, 10, 20].map((vatRate) => <MenuItem value={vatRate / 100} key={vatRate}>{vatRate}%</MenuItem>),
    [],
  );

  const shelfLifeDurationTypes = useMemo(() => (
    [
      <MenuItem value={Duration_Type_Enum.Days} key={Duration_Type_Enum.Days}>{t('common:day_s')}</MenuItem>,
      <MenuItem value={Duration_Type_Enum.Months} key={Duration_Type_Enum.Months}>{t('common:months').toLowerCase()}</MenuItem>,
      <MenuItem value={Duration_Type_Enum.Years} key={Duration_Type_Enum.Years}>{t('common:year_s')}</MenuItem>,
    ]
  ), [t]);

  return (
    <>
      <Grid size={{ xs: 12 }} container spacing={2} sx={{ mb: 2 }}>
        <Grid
          size={{ xs: 12, sm: 'grow' }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="titleMedium">
            {t('products:vat_rate')}*
          </Typography>
          <Typography variant="bodySmall">
            {t('products:vat_rate_description')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Controller
            name="vat_rate"
            control={control}
            rules={{
              validate: {
                required: (value) => !!value || (t('validation:this_field_is_required') as string),
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  size="small"
                  fullWidth
                  variant="outlined"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  displayEmpty
                  renderValue={(selected) => (
                    !selected
                      ? <Typography sx={{color:'placeholder.main'}}>{t('products:vat_rate_placeholder')}</Typography>
                      : `${selected * 100}%`
                  )}
                  error={!!fieldState.error}
                >
                  {vatRateValues}
                </Select>
                {!!fieldState.error
                  && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error?.message}</FormHelperText>}
              </>
            )}
          />
        </Grid>
      </Grid>

      {isFoodProduct && (
        <Grid size={{ xs: 12 }}>
          <Accordion
            expanded={conservationExpanded}
            onChange={(event, expanded) => setConservationExpanded(expanded)}
            disableGutters
            sx={{
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'outlineVariant.main',
              borderRadius: '8px !important',
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                py: 0.5,
              }}
            >
              <Typography variant="titleSmall">
                {t('products:conservation')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                borderTop: '1px solid',
                borderColor: 'outlineVariant.main',
                px: 2,
                pb: 2,
                pt: 2,
              }}
            >
              <Grid container rowSpacing={4}>
                <Grid size={{ xs: 12 }} container spacing={2}>
                  <Grid
                    size={{ xs: 12, sm: 'grow' }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="titleMedium">
                      {t('products:shelf_life')}
                    </Typography>
                    <Typography variant="bodySmall">
                      {t('products:shelf_life_description')}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, lg: 5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                      }}
                    >
                      <Controller
                        name="shelf_life_duration"
                        control={control}
                        rules={{
                          pattern: /^\d+$/,
                          validate: (v) => v === null || v === undefined || v > 0,
                        }}
                        render={({ field, fieldState }) => (
                          <>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              placeholder={t('products:shelf_life')}
                              name={field.name}
                              value={field.value}
                              onChange={field.onChange}
                            />
                            {!!fieldState.error && <FormHelperText sx={{ position: 'absolute', mt: 6 }} error>{t('error:number_must_be_whole_and_positive')}</FormHelperText>}
                          </>
                        )}
                      />
                      <Controller
                        name="shelf_life_duration_type"
                        control={control}
                        render={({ field }) => (
                          <Select
                            variant="outlined"
                            size="small"
                            MenuProps={{ disablePortal: true }}
                            renderValue={(value) => (<Typography sx={{ mt: 0.5 }}>{t(`common:${value?.toLowerCase()}`, { count: 2 })}</Typography>)}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                          >
                            {shelfLifeDurationTypes}
                          </Select>
                        )}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }} container spacing={2}>
                    <Grid
                      size={{ xs: 12, sm: 'grow' }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="titleMedium">
                        {t('products:preservation_method')}
                      </Typography>
                      <Typography variant="bodySmall">
                        {t('products:preservation_method_description')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 5 }}>
                      <Controller
                        name="preservation_method"
                        control={control}
                        render={({ field }) => (
                          <Select
                            variant="outlined"
                            size="small"
                            fullWidth
                            MenuProps={{ disablePortal: true }}
                            displayEmpty
                            renderValue={(value) => (
                              value && <Typography>{t(`products:preservation_method_${value?.toLowerCase()}`)}</Typography>
                            )}
                            {...field}
                          >
                            <MenuItem value={Preservation_Method_Enum.None} key={Preservation_Method_Enum.None}>{t('products:preservation_method_none')}</MenuItem>
                            <MenuItem value={Preservation_Method_Enum.Refrigerated} key={Preservation_Method_Enum.Refrigerated}>{t('products:preservation_method_refrigerated')}</MenuItem>
                            <MenuItem value={Preservation_Method_Enum.Frozen} key={Preservation_Method_Enum.Frozen}>{t('products:preservation_method_frozen')}</MenuItem>
                          </Select>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <Accordion
          expanded={ingredientsAllergensExpanded}
          onChange={(event, expanded) => setIngredientsAllergensExpanded(expanded)}
          disableGutters
          sx={{
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'outlineVariant.main',
            borderRadius: '8px !important',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              py: 0.5,
              [`& .${accordionSummaryClasses.content}`]: {
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                alignItems: {
                  xs: 'flex-start',
                  sm: 'center',
                },
                gap: {
                  xs: 0.5,
                  sm: 2,
                },
              },
            }}
          >
            <Typography variant="titleSmall" sx={{ flexGrow: 1 }}>
              {t('products:ingredients_and_allergens')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: 'tertiaryContainer.main',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <span>🚗</span>
              <Typography variant="bodySmall" sx={{ color: 'onTertiaryContainer' }}>
                {t('products:drive_visibility_badge')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              borderTop: '1px solid',
              borderColor: 'outlineVariant.main',
              px: 2,
              pb: 2,
              pt: 2,
            }}
          >
            <Grid container rowSpacing={4}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="titleMedium" sx={{ mb: 0.5 }}>
                  {t('products:ingredient_list')}
                </Typography>
                <Typography variant="bodySmall" sx={{ mb: 2 }}>
                  {t('products:ingredient_list_description')}
                </Typography>
                <Controller
                  name="ingredients"
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <TextField
                        fullWidth
                        variant="outlined"
                        slotProps={{
                          htmlInput: {
                            style: {
                              minHeight: 80,
                            },
                          },
                        }}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={t('products:ingredient_list_placeholder')}
                        multiline
                        error={!!fieldState.error}
                        disabled={!!isExemptFromIngredientList}
                      />
                      {fieldState.error
                        && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error?.message}</FormHelperText>}
                    </>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }} sx={{ mt: -2 }}>
                <Controller
                  name="is_exempt_from_ingredient_list"
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={(
                          <Checkbox
                            checked={field.value || false}
                            name={field.name}
                            onChange={(event) => field.onChange(event.target.checked ? true : null)}
                          />
                        )}
                        label={<Typography variant="titleSmall">{t('products:is_exempt_from_ingredient_list')}</Typography>}
                      />
                      <Button variant="text" onClick={() => window.Intercom('showArticle', IntercomArticlesIds.ExemptFromIngredientList)}>
                        <InfoOutlinedIcon />
                      </Button>
                    </Box>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }} container spacing={2}>
                <Grid
                  size={{ xs: 12, sm: 'grow' }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="titleMedium">
                    {t('products:allergen_list')}
                  </Typography>
                  <Typography variant="bodySmall">
                    {t('products:allergen_list_description')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, lg: 5 }}>
                  <Controller
                    name="allergens"
                    control={control}
                    render={({ field, fieldState }) => (
                      <>
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t('products:allergen_list_placeholder')}
                          multiline
                          error={!!fieldState.error}
                          disabled={!!hasNoAllergenDeclaration}
                        />
                        {fieldState.error
                          && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error?.message}</FormHelperText>}
                      </>
                    )}
                  />
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }} sx={{ mt: -2 }}>
                <Controller
                  name="has_no_allergen_declaration"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={field.value || false}
                          onChange={(e) => field.onChange(e.target.checked ? true : null)}
                          name={field.name}
                        />
                      )}
                      label={<Typography variant="titleSmall">{t('products:has_no_allergen_declaration')}</Typography>}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {isFoodProduct && (
        <Grid size={{ xs: 12 }}>
          <Accordion
            expanded={nutritionalInfoExpanded}
            onChange={(event, expanded) => setNutritionalInfoExpanded(expanded)}
            disableGutters
            sx={{
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'outlineVariant.main',
              borderRadius: '8px !important',
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                py: 0.5,
              }}
            >
              <Typography variant="titleSmall">
                {t('products:nutritional_information')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                borderTop: '1px solid',
                borderColor: 'outlineVariant.main',
                px: 2,
                pb: 2,
                pt: 2,
              }}
            >
              <NutritionFactsForm />
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
        <Box
          sx={{
            backgroundColor: 'tertiaryContainer.main',
            px: 3,
            py: 2,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <span>💡</span>
          <Typography variant="bodySmall" sx={{ color: 'inherit' }}>
            {t('products:tip_product_characteristics')}
          </Typography>
        </Box>
      </Grid>

      <Divider sx={{ width: '100%', mt: 2 }} />
    </>
  );
}
